import { authApi } from "@/domains/shared/apis/authApi";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";

const CSRF_HEADER = "X-XSRF-TOKEN";
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

export const ANTIFORGERY_INVALID_DETAIL = "Token antiforgery inválido ou ausente.";

let csrfToken: string | null = null;
let csrfFetch: Promise<string> | null = null;
let csrfRequiredOverride: boolean | undefined;

/** @internal Allows unit tests to assert production CSRF behavior under Vitest. */
export function setCsrfRequiredOverride(value: boolean | undefined): void {
    csrfRequiredOverride = value;
}

/** Production builds enforce CSRF; local dev matches relaxed backend antiforgery. */
export function isCsrfRequired(): boolean {
    if (csrfRequiredOverride !== undefined) {
        return csrfRequiredOverride;
    }

    return import.meta.env.DEV !== true;
}

export function resetCsrfToken(): void {
    csrfToken = null;
    csrfFetch = null;
    csrfRequiredOverride = undefined;
}

export async function ensureCsrfToken(): Promise<string> {
    if (!isCsrfRequired()) {
        return "";
    }

    if (csrfToken) {
        return csrfToken;
    }

    if (!csrfFetch) {
        csrfFetch = loadCsrfTokenFromApi()
            .finally(() => {
                csrfFetch = null;
            });
    }

    return csrfFetch;
}

async function loadCsrfTokenFromApi(): Promise<string> {
    const { data } = await authApi.getAntiforgery();
    const token = data.token?.trim();
    if (!token) {
        throw new Error("CSRF token missing in response.");
    }
    csrfToken = token;
    return token;
}

export function isAntiforgeryError(detail: unknown): boolean {
    return detail === ANTIFORGERY_INVALID_DETAIL;
}

function isMutating(config: InternalAxiosRequestConfig): boolean {
    const method = config.method?.toLowerCase() ?? "get";
    return MUTATING_METHODS.has(method);
}

export function setupCsrfInterceptor(api: AxiosInstance): void {
    if (!isCsrfRequired()) {
        return;
    }

    api.interceptors.request.use(async (config) => {
        if (!isMutating(config)) {
            return config;
        }

        const token = await ensureCsrfToken();
        config.headers.set(CSRF_HEADER, token);
        return config;
    });

    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error?.response?.status;
            const original = error?.config as InternalAxiosRequestConfig & { _csrfRetry?: boolean };

            const detail = (error?.response?.data as { detail?: unknown } | undefined)?.detail;
            const shouldRetryCsrf =
                status === 400
                && original
                && isMutating(original)
                && !original._csrfRetry
                && isAntiforgeryError(detail);

            if (shouldRetryCsrf) {
                resetCsrfToken();
                original._csrfRetry = true;
                original.headers.set(CSRF_HEADER, await ensureCsrfToken());
                return api.request(original);
            }

            return Promise.reject(error);
        },
    );
}
